import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ensureControllersRestored,
  getProControllerManager,
  type ProController,
  type ProControllerState,
} from '@/lib/pro-controller';

interface UseProControllers {
  /** Whether WebHID exists in this browser (Chromium-only). */
  supported: boolean;
  /** Number of currently-open controllers. */
  count: number;
  /** Prompt for device authorization — must be called from a user gesture. */
  connect: () => Promise<void>;
  /** Live per-controller state, read inside a requestAnimationFrame loop. */
  snapshotsRef: React.RefObject<ProControllerState[]>;
}

// Subscribes to the shared ProControllerManager singleton and exposes decoded
// state through a ref so a polling loop can read it without re-rendering on
// every input report. Controllers are owned by the singleton, so this hook only
// attaches/detaches its own listeners — it never opens or closes devices. That
// keeps granted controllers alive across page navigation and strict-mode
// remounts, so the user pairs once and reconnects are silent thereafter.
export function useProControllers(): UseProControllers {
  const [supported, setSupported] = useState(false);
  const [count, setCount] = useState(0);
  const statesRef = useRef<Map<ProController, ProControllerState>>(new Map());
  const snapshotsRef = useRef<ProControllerState[]>([]);

  const syncSnapshots = useCallback(() => {
    snapshotsRef.current = Array.from(statesRef.current.values());
  }, []);

  useEffect(() => {
    const manager = getProControllerManager();
    if (!manager) {
      return;
    }
    setSupported(true);

    const inputListeners = new Map<ProController, (event: Event) => void>();

    const attach = (controller: ProController) => {
      if (inputListeners.has(controller)) {
        return;
      }
      const listener = (event: Event) => {
        statesRef.current.set(
          controller,
          (event as CustomEvent<ProControllerState>).detail,
        );
        syncSnapshots();
      };
      inputListeners.set(controller, listener);
      controller.addEventListener('input', listener);
      // Seed from the last decoded state so a controller opened on a previous
      // page is visible immediately, before its next input report.
      if (controller.lastState) {
        statesRef.current.set(controller, controller.lastState);
      }
    };

    const detach = (controller: ProController) => {
      const listener = inputListeners.get(controller);
      if (listener) {
        controller.removeEventListener('input', listener);
        inputListeners.delete(controller);
      }
      statesRef.current.delete(controller);
      syncSnapshots();
    };

    const onAdd = (event: Event) => {
      attach((event as CustomEvent<ProController>).detail);
      syncSnapshots();
      setCount(manager.controllers.length);
    };

    const onRemove = (event: Event) => {
      detach((event as CustomEvent<ProController>).detail);
      setCount(manager.controllers.length);
    };

    manager.addEventListener('add', onAdd);
    manager.addEventListener('remove', onRemove);

    // Pick up controllers already open from a previous page/mount.
    for (const controller of manager.controllers) {
      attach(controller);
    }
    syncSnapshots();
    setCount(manager.controllers.length);

    void ensureControllersRestored();

    return () => {
      manager.removeEventListener('add', onAdd);
      manager.removeEventListener('remove', onRemove);
      for (const controller of Array.from(inputListeners.keys())) {
        detach(controller);
      }
    };
  }, [syncSnapshots]);

  const connect = useCallback(async () => {
    await getProControllerManager()?.connect();
  }, []);

  return { supported, count, connect, snapshotsRef };
}
